import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  BookOpen,
  Calendar,
  FileText,
  Flame,
  GitBranch,
  Lock,
  Mic,
  PenLine,
  QrCode,
  Share2,
  Shield,
  UserPlus,
  Users,
  MessageSquare,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PUBLIC_DEMO_ROUTE } from '../../../utils/publicNavigation';
import {
  DEMO_WAVE_BARS,
  FAMILY_CONNECTION_SAMPLES,
  POSSESSION_SAMPLES,
  PRIVACY_TRUST_ITEMS,
  STORY_PROMPTS,
  WHY_IT_MATTERS_CARDS,
} from '../../../content/howItWorksDemoContent';
import { DemoPanel, DemoSection, IllustrativeLabel } from './DemoChrome';
import { IllustratedArchivePreview } from './IllustratedArchivePreview';

const cardIcons = {
  voice: Mic,
  document: FileText,
  story: PenLine,
};

export function WhyItMattersSection({ compact = false }) {
  return (
    <DemoSection
      id="why-it-matters"
      eyebrow="Why it matters"
      title="Family history is too easy to lose."
      copy={
        compact
          ? 'Stories fade. Photos scatter. MyLegacyBridge gives families one calm, private place to keep what matters together.'
          : 'Stories fade. Photos get scattered. Important documents end up buried in drawers, inboxes and old boxes. MyLegacyBridge gives families one calm, private place to keep what matters together.'
      }
    >
      <IllustrativeLabel className="mb-3" />
      <div className={cn('grid gap-4', compact ? 'sm:grid-cols-3' : 'md:grid-cols-3')}>
        {WHY_IT_MATTERS_CARDS.map((item) => {
          const Icon = cardIcons[item.id] || FileText;
          return (
            <DemoPanel key={item.id} title={item.title}>
              <Icon className="mb-2 h-5 w-5 text-[hsl(38_36%_42%)]" strokeWidth={1.75} aria-hidden />
              <p className="font-serif text-sm italic leading-relaxed text-[hsl(24_14%_18%)]">{item.sample}</p>
              <p className="mt-3 text-xs text-[hsl(32_10%_42%)]">{item.meta}</p>
            </DemoPanel>
          );
        })}
      </div>
    </DemoSection>
  );
}

export function StepInsideArchiveSection() {
  return (
    <section
      id="sample-archive"
      className="product-demo-archive-showcase scroll-mt-24"
      aria-labelledby="step-inside-archive-heading"
    >
      <div className="mx-auto w-full max-w-[75rem] px-4 sm:px-6 lg:px-8">
        <header className="product-demo-archive-showcase__header">
          <p className="product-demo-eyebrow">Inside your archive</p>
          <h2 id="step-inside-archive-heading" className="product-demo-heading mt-2">
            Step inside a private family archive
          </h2>
          <p className="product-demo-copy mt-3 mx-auto max-w-2xl">
            See how stories, voices, documents and family connections sit together in one calm private space.
          </p>
        </header>

        <IllustratedArchivePreview variant="featured" showCaption showLabel />
      </div>
    </section>
  );
}

export function SampleArchiveSection({ compact = false }) {
  if (compact) {
    return (
      <DemoSection
        id="sample-archive"
        eyebrow="One private archive"
        title="A private family archive in one view"
        copy={null}
      >
        <IllustrativeLabel className="mb-3" />
        <IllustratedArchivePreview variant="compact" showLabel={false} />
      </DemoSection>
    );
  }

  return <StepInsideArchiveSection />;
}

export function StoryPromptsSection({ compact = false }) {
  return (
    <DemoSection
      id="story-prompts"
      eyebrow="Storybook & memories"
      title="A memory can start with one question"
      copy={
        compact
          ? 'Begin with a question, a few lines, a voice note or one photograph.'
          : 'Users can begin with a question, a few lines, a voice note or one photograph — then grow the archive over time.'
      }
    >
      <IllustrativeLabel className="mb-3" />
      <div className="grid gap-3 sm:grid-cols-3">
        {STORY_PROMPTS.map((prompt, i) => (
          <DemoPanel key={prompt} title={`Prompt ${i + 1}`}>
            <BookOpen className="mb-2 h-5 w-5 text-[hsl(38_36%_42%)]" strokeWidth={1.75} aria-hidden />
            <p className="text-sm leading-relaxed text-[hsl(24_14%_16%)]">{prompt}</p>
          </DemoPanel>
        ))}
      </div>
    </DemoSection>
  );
}

export function VoiceMemorySection({ compact = false }) {
  return (
    <DemoSection
      id="voice-memories"
      eyebrow="Voice memories"
      title="Hear the voice behind the memory"
      copy={compact ? null : 'Record naturally. Transcribe when you want editable text. Everything stays in your private archive.'}
    >
      <IllustrativeLabel className="mb-3" />
      <DemoPanel title="Voice memory" badge="Private" className="max-w-md">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(152_28%_22%_/_0.1)] text-[hsl(152_28%_22%)]">
            <Mic className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div>
            <p className="text-sm font-medium text-[hsl(24_14%_16%)]">Kitchen stories</p>
            <p className="text-xs text-[hsl(32_10%_42%)]">Eleanor Morrison</p>
          </div>
        </div>
        <div className="mt-4 flex h-12 items-end justify-center gap-[3px]">
          {DEMO_WAVE_BARS.map((h, i) => (
            <span key={i} className="product-demo-wave-bar" style={{ height: `${h}px` }} />
          ))}
        </div>
        <p className="mt-3 text-center text-xs text-[hsl(32_10%_42%)]">3:42 · Transcript available</p>
        {!compact && (
          <p className="mt-2 text-center text-[11px] text-[hsl(32_10%_48%)]">Optional AI transcription · kept with the archive</p>
        )}
      </DemoPanel>
    </DemoSection>
  );
}

export function DocumentsPossessionsSection() {
  return (
    <DemoSection
      id="documents-possessions"
      eyebrow="Documents & keepsakes"
      title="Keep the things that carried meaning"
      copy="Preserve documents, keepsakes and possessions that tell the story behind a life."
    >
      <IllustrativeLabel className="mb-3" />
      <div className="grid gap-3 sm:grid-cols-2">
        {POSSESSION_SAMPLES.map((item) => (
          <DemoPanel key={item.label} title="Preserved item">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[hsl(38_14%_88%)] bg-[hsl(42_28%_97%)]">
                <FileText className="h-4 w-4 text-[hsl(38_36%_42%)]" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-sm font-medium text-[hsl(24_14%_16%)]">{item.label}</p>
                <p className="mt-0.5 text-xs text-[hsl(32_10%_42%)]">{item.meta}</p>
              </div>
            </div>
          </DemoPanel>
        ))}
      </div>
    </DemoSection>
  );
}

export function FamilyConnectionsSection() {
  return (
    <DemoSection
      id="family-connections"
      eyebrow="Connections & reminders"
      title="Keep family connected, not just recorded"
      copy="Link relatives, verify connections, set reminders and invite family when you are ready."
    >
      <IllustrativeLabel className="mb-3" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FAMILY_CONNECTION_SAMPLES.map((item) => {
          const icons = { 'Family tree': GitBranch, 'Birthday reminder': Calendar, 'Remembrance day': Bell, 'Invite family': UserPlus };
          const Icon = icons[item.label] || Users;
          return (
            <DemoPanel key={item.label} title={item.label}>
              <Icon className="mb-2 h-5 w-5 text-[hsl(152_28%_22%)]" strokeWidth={1.75} aria-hidden />
              <p className="text-xs leading-relaxed text-[hsl(32_10%_42%)]">{item.detail}</p>
            </DemoPanel>
          );
        })}
      </div>
    </DemoSection>
  );
}

export function LegacyMemorialSection({ compact = false, className }) {
  return (
    <DemoSection
      id="legacy-memorial"
      eyebrow="Keepsakes & remembrance"
      title="Legacy Books and Memorial Bridge"
      copy={compact ? null : 'Legacy Book PDFs, Memorial Bridge tribute pages, QR keepsakes — when your family chooses.'}
      className={className}
    >
      <IllustrativeLabel className="product-demo-keepsake-label" />
      <div className="product-demo-keepsake-grid">
        <DemoPanel title="Legacy Book PDF" className="product-demo-keepsake-card">
          <div className="product-demo-keepsake-card__icon">
            <BookOpen className="h-5 w-5 text-[#c9a961]" strokeWidth={1.75} aria-hidden />
          </div>
          <p className="font-serif text-sm text-[hsl(24_14%_16%)]">The Morrison Family Archive</p>
          <p className="mt-1 text-xs text-[hsl(32_10%_42%)]">Stories · voices · documents</p>
          <Download className="mt-auto h-4 w-4 text-[#c9a961]/70" aria-hidden />
        </DemoPanel>
        <DemoPanel title="Memorial Bridge" className="product-demo-keepsake-card">
          <div className="product-demo-keepsake-card__icon">
            <Flame className="h-5 w-5 text-[#c9a961]" strokeWidth={1.75} aria-hidden />
          </div>
          <p className="font-serif text-sm text-[hsl(24_14%_16%)]">Remembered with love</p>
          <p className="mt-1 text-xs text-[hsl(32_10%_42%)]">Private draft until you publish</p>
        </DemoPanel>
        {!compact && (
          <>
            <DemoPanel title="Tribute wall" className="product-demo-keepsake-card">
              <div className="product-demo-keepsake-card__icon">
                <MessageSquare className="h-5 w-5 text-[#c9a961]" strokeWidth={1.75} aria-hidden />
              </div>
              <p className="rounded-lg bg-[hsl(42_28%_97%)] px-3 py-2.5 text-xs italic leading-relaxed text-[hsl(32_10%_42%)]">
                &ldquo;Thank you for the Sunday roasts and the stories.&rdquo;
              </p>
            </DemoPanel>
            <DemoPanel title="QR keepsake" className="product-demo-keepsake-card">
              <div className="product-demo-keepsake-card__icon">
                <QrCode className="h-5 w-5 text-[#c9a961]" strokeWidth={1.25} aria-hidden />
              </div>
              <p className="text-sm font-medium text-[hsl(24_14%_16%)]">Scan to remember</p>
              <p className="mt-1 text-xs text-[hsl(32_10%_42%)]">Link to published memorial</p>
            </DemoPanel>
          </>
        )}
      </div>
    </DemoSection>
  );
}

const trustIcons = [Lock, Share2, Download, Flame, Shield];

export function PrivacyTrustSection() {
  return (
    <section id="privacy-trust" className="product-demo-trust-band scroll-mt-24" aria-labelledby="privacy-trust-heading">
      <div className="product-demo-trust-band__inner">
        <header className="product-demo-trust-band__intro">
          <p className="product-demo-eyebrow">Privacy & trust</p>
          <h2 id="privacy-trust-heading" className="product-demo-heading">
            Private by default — you stay in control
          </h2>
          <p className="product-demo-copy">
            Nothing is public until you publish. Export, invite and memorial pages are always your choice.
          </p>
        </header>
        <ul className="product-demo-trust-grid">
          {PRIVACY_TRUST_ITEMS.map((line, index) => {
            const Icon = trustIcons[index] || Shield;
            return (
              <li key={line} className="product-demo-trust-card">
                <Icon className="product-demo-trust-card__icon" strokeWidth={1.75} aria-hidden />
                <p className="product-demo-trust-card__text">{line}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function HomepageDemoTeaser() {
  const previewCards = WHY_IT_MATTERS_CARDS.slice(0, 3);

  return (
    <section id="see-how-it-works" className="homepage-demo-teaser scroll-mt-24">
      <div className="homepage-demo-teaser__shell">
        <div className="homepage-demo-teaser__layout">
          <div className="homepage-demo-teaser__content">
            <header className="homepage-demo-teaser__header">
              <p className="product-demo-eyebrow">See how it works</p>
              <h2 className="homepage-demo-teaser__title">
                See how a family archive comes to life
              </h2>
              <p className="homepage-demo-teaser__copy">
                From a single story or voice note to a full archive, here is how MyLegacyBridge works.
              </p>
            </header>

            <div className="homepage-demo-teaser__card-grid homepage-demo-teaser__card-grid--3 homepage-demo-teaser__preview-grid">
              {previewCards.map((item) => {
                const Icon = cardIcons[item.id] || FileText;
                return (
                  <article key={item.id} className="homepage-demo-teaser__card">
                    <span className="homepage-demo-teaser__card-icon">
                      <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                    </span>
                    <p className="homepage-demo-teaser__card-label">{item.title}</p>
                    <p className="homepage-demo-teaser__card-sample">{item.sample}</p>
                  </article>
                );
              })}
            </div>

            <div className="homepage-demo-teaser__actions">
              <Link to={PUBLIC_DEMO_ROUTE} className="homepage-demo-teaser__btn-primary" data-testid="homepage-demo-view-full-demo">
                View full demo
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </div>

          <aside className="homepage-demo-teaser__featured" aria-label="Illustrative archive preview">
            <IllustratedArchivePreview variant="compact" />
          </aside>
        </div>
      </div>
    </section>
  );
}